import { useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAddInteraction } from "@/hooks/useLeads";
import { cn } from "@/lib/utils";

interface WhatsAppTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lead: {
    id: string;
    name: string;
    phone: string;
  };
}

const templates = [
  {
    id: "follow-up",
    name: "Follow-up de proposta",
    message: `Oi {{nome}}, tudo bem? Passando para saber se você conseguiu revisar a proposta. Qualquer ajuste de escopo ou prazo a gente fecha por aqui mesmo — é só me dizer.`,
  },
  {
    id: "first-contact",
    name: "Primeiro contato",
    message: `Olá {{nome}}! Aqui é o Eliveuton. Vi seu contato pelo site e queria entender melhor o que você precisa resolver. Tem um horário hoje ou amanhã pra uma conversa rápida de 15 minutos?`,
  },
  {
    id: "billing",
    name: "Cobrança amigável",
    message: `Oi {{nome}}, tudo certo? Só um lembrete: a parcela do projeto vence em breve. Se já tiver feito o pagamento, pode ignorar esta mensagem. Qualquer dúvida, estou por aqui.`,
  },
];

const VARS = [
  {
    token: "{{nome}}",
    get: (lead: WhatsAppTemplateDialogProps["lead"]) => lead.name.split(" ")[0],
  },
  { token: "{{telefone}}", get: (lead: WhatsAppTemplateDialogProps["lead"]) => lead.phone },
  { token: "{{hoje}}", get: () => new Date().toLocaleDateString("pt-BR") },
];

function render(text: string, lead: WhatsAppTemplateDialogProps["lead"]) {
  return VARS.reduce((acc, v) => acc.split(v.token).join(v.get(lead)), text);
}

export function WhatsAppTemplateDialog({ open, onOpenChange, lead }: WhatsAppTemplateDialogProps) {
  const [selectedTemplate, setSelectedTemplate] = useState(templates[0].id);
  const [message, setMessage] = useState(templates[0].message);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const addInteraction = useAddInteraction();

  const rendered = render(message, lead);

  const handleTemplateChange = (templateId: string) => {
    setSelectedTemplate(templateId);
    const template = templates.find((t) => t.id === templateId);
    if (template) setMessage(template.message);
  };

  const insertVar = (token: string) => {
    const el = textareaRef.current;
    if (!el) {
      setMessage((m) => m + token);
      return;
    }
    const start = el.selectionStart ?? message.length;
    const end = el.selectionEnd ?? message.length;
    const next = message.slice(0, start) + token + message.slice(end);
    setMessage(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const handleOpenWhatsApp = () => {
    const phone = lead.phone.replace(/\D/g, "");
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(rendered)}`, "_blank");
    addInteraction.mutate({
      leadId: lead.id,
      type: "whatsapp",
      content: rendered,
      metadata: { phone: lead.phone, template: selectedTemplate },
    });
    toast.success("WhatsApp aberto e interação registrada!");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[560px] gap-0 overflow-hidden rounded-[18px] p-0">
        <DialogHeader className="space-y-1 border-b border-border px-6 pb-4 pt-[22px] text-left">
          <DialogTitle className="text-[17px] font-bold tracking-[-0.02em]">
            Mensagem de WhatsApp
          </DialogTitle>
          <DialogDescription className="text-[12.5px]">
            Para {lead.name} · {lead.phone}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 px-6 py-5">
          <div className="flex flex-wrap gap-[7px]">
            {templates.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => handleTemplateChange(t.id)}
                className={cn(
                  "rounded-full border px-[13px] py-[7px] text-xs transition-colors",
                  selectedTemplate === t.id
                    ? "border-primary bg-accent font-bold text-primary"
                    : "border-border font-semibold text-muted-foreground hover:bg-muted",
                )}
              >
                {t.name}
              </button>
            ))}
          </div>

          <div className="rounded-[14px_14px_14px_4px] bg-ok-soft">
            <Textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Digite sua mensagem..."
              rows={5}
              className="resize-none border-0 bg-transparent px-[18px] py-4 text-[13px] leading-[1.65] text-foreground shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
            />
          </div>

          <div className="flex flex-wrap items-center gap-[9px] text-[11.5px] text-muted-foreground">
            <span>Variáveis:</span>
            {VARS.map((v) => (
              <button
                key={v.token}
                type="button"
                onClick={() => insertVar(v.token)}
                className="rounded-md border border-border bg-muted px-[7px] py-[3px] font-mono text-foreground hover:border-primary/40"
              >
                {v.token}
              </button>
            ))}
            <span className="ml-auto">{rendered.length} caracteres</span>
          </div>
        </div>

        <div className="flex justify-end gap-[9px] border-t border-border px-6 py-4">
          <Button
            variant="outline"
            className="h-[38px] rounded-[10px] text-[12.5px] font-semibold"
            onClick={() => onOpenChange(false)}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleOpenWhatsApp}
            disabled={!rendered.trim()}
            className="h-[38px] rounded-[10px] bg-ok text-[12.5px] font-semibold text-ok-foreground hover:bg-ok/90"
          >
            Abrir no WhatsApp
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
